import { MainLayout } from '@components/layouts/main.layout';
import { ReadinessTab } from '@components/pages/technology/components/shared/readiness-tab.component';
import { TechnologyTabLayout } from '@components/pages/technology/components/shared/technology-tab-layout.component';
import { TechnologyHeader } from '@components/pages/technology/components/technology-header.component';
import { TechnologyMetadataTab } from '@components/pages/technology/components/technology-metadata-tab.component';
import { TechnologyOrganizationTab } from '@components/pages/technology/components/technology-organization-tab.component';
import { TechnologyOverviewTab } from '@components/pages/technology/components/technology-overview-tab.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { useGetTechnology } from '@queries/technology.query';
import { TechnologyHeaderTab, parseAsTechnologyHeaderTab } from '@utils/query-state.util';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryState } from 'nuqs';
import { Suspense } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MaContextColumn } from './components/ma-context-column.component';
import { MissionAssessmentForm } from './components/mission-assessment-form.component';

export const MissionAssessmentCreatePage: React.FC = () => {
  const { technologyId } = useParams<{ technologyId: string }>();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId') || undefined;
  const [selectedTab, setSelectedTab] = useQueryState('tab', parseAsTechnologyHeaderTab.withDefault('ma'));
  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);

  const onToggleTab = (tab: TechnologyHeaderTab) => {
    void setSelectedTab(tab);
  };

  const renderContent = () => {
    if (!technologyId) {
      return (
        <EmptyStateContainer
          message="Technology ID is missing."
          title="Technology Not Found"
        />
      );
    }

    switch (selectedTab) {
      case 'overview':
        return (
          <TechnologyOverviewTab
            onNavigateToTab={onToggleTab}
            technologyId={technologyId}
          />
        );
      case 'details':
        return <TechnologyMetadataTab technologyId={technologyId} />;
      case 'organization':
        if (isTechnologyLoading) {
          return (
            <Tile
              isLoading={true}
              loadingState={{ message: 'Loading organization...' }}
            />
          );
        }
        return <TechnologyOrganizationTab technology={technology} />;
      case 'trl':
        return <ReadinessTab type="trl" />;
      case 'mrl':
        return <ReadinessTab type="mrl" />;
      case 'ma':
        if (!requestId) {
          return (
            <EmptyStateContainer
              message="Missing request ID."
              title="Unable to Load Assessment"
            />
          );
        }
        return (
          <Suspense>
            <TechnologyTabLayout
              contextColumn={
                <MaContextColumn
                  requestId={requestId}
                  technologyId={technologyId}
                />
              }
              mainContent={
                <MissionAssessmentForm
                  mode="create"
                  requestId={requestId}
                  technologyId={technologyId}
                />
              }
              variant="content-context"
            />
          </Suspense>
        );
      default:
        return null;
    }
  };

  return (
    <PageviewTracker>
      <HelmetWrapper title="Mission Assessment" />
      <MainLayout
        fullWidth
        className="max-w-full"
      >
        <div className="w-full flex flex-col gap-4">
          {/* Tech Header - full width */}
          <TechnologyHeader
            onToggleTab={onToggleTab}
            selectedTab={selectedTab}
          />

          {/* Content area */}
          <div className="@container/technology mx-auto w-full max-w-[1600px] px-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </MainLayout>
    </PageviewTracker>
  );
};
